import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../useAuth";
import { BiMenu } from "react-icons/bi";
import { FaUserCircle, FaSignOutAlt, FaExternalLinkAlt, FaCrown } from "react-icons/fa";
import { Drawer } from "@mui/material";
import NavBar from "./NavBar";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const pathname = location.pathname?.split("/")?.[2] || "Dashboard";

  const formatTitle = (path) => {
    switch (path) {
      case "carouselManager":
        return "Carousel Manager";
      case "categoryManager":
        return "Category Manager";
      case "subCategoryManager":
        return "SubCategory Manager";
      case "galleryManager":
        return "Gallery Manager";
      case "testimonialManager":
        return "Testimonial Manager";
      case "bookingRequests":
        return "Booking Requests";
      case "pricingManager":
        return "Pricing Manager";
      case "usersManagement":
        return "Users Management";
      case "profile":
        return "My Profile";
      default:
        return "Admin Dashboard";
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  return (
    <header className="bg-[#121215]/95 backdrop-blur-xl border-b border-[#C8A165]/20 px-6 py-3 shadow-lg sticky top-0 z-40">
      <div className="flex items-center justify-between">
        {/* Left Side: Title / Mobile Toggle */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleDrawerToggle}
            className="lg:hidden p-2 rounded-xl text-cream/70 hover:bg-white/5 hover:text-gold transition-colors"
            aria-label="Toggle Menu"
          >
            <BiMenu size={24} />
          </button>

          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-cream capitalize tracking-wide">
                {formatTitle(pathname)}
              </h1>
              <span className="bg-[#C8A165]/15 text-[#C8A165] border border-[#C8A165]/30 text-[0.65rem] font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1 uppercase tracking-wider">
                <FaCrown size={10} /> Live API Mode
              </span>
            </div>
            <p className="text-xs text-cream/50 hidden sm:block mt-0.5">
              Tanushree Saha Bridal Studio Management
            </p>
          </div>
        </div>

        {/* Right Side: Quick Links & Profile Info */}
        <div className="flex items-center gap-4">
          {/* Quick link to main website */}
          <a
            href="http://localhost:3000"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-[#C8A165] bg-[#C8A165]/15 hover:bg-[#C8A165] hover:text-[#0A0A0C] border border-[#C8A165]/30 px-3.5 py-1.5 rounded-xl transition-all"
          >
            <span>View Website</span>
            <FaExternalLinkAlt size={10} />
          </a>

          {/* Profile User Badge */}
          <div className="flex items-center gap-3 pl-4 border-l border-[#C8A165]/20">
            <div
              onClick={() => navigate("profile")}
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-[#C8A165] to-[#F5E5C9] text-[#0A0A0C] flex items-center justify-center font-bold text-sm shadow-md group-hover:scale-105 transition-all">
                {user?.name?.[0]?.toUpperCase() || <FaUserCircle size={20} />}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-xs font-bold text-cream capitalize group-hover:text-gold transition-colors">
                  {user?.name || "Administrator"}
                </p>
                <p className="text-[0.62rem] text-[#C8A165] tracking-wider uppercase font-medium">
                  {user?.userType || "Admin"}
                </p>
              </div>
            </div>

            {/* Logout button */}
            <button
              onClick={() => {
                if (window.confirm("Are you sure you want to log out?")) {
                  logout();
                  navigate("/login");
                }
              }}
              className="p-2 text-cream/40 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-colors cursor-pointer ml-1"
              title="Logout"
            >
              <FaSignOutAlt size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 260,
            backgroundColor: "#0E0E11",
            color: "#F8F5F2",
            borderRight: "1px solid rgba(200, 161, 101, 0.2)",
          },
        }}
      >
        <NavBar />
      </Drawer>
    </header>
  );
}
